import React, { useState, useEffect } from "react";
import { TextField, Button, MenuItem, Typography, Box, Alert } from "@mui/material";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";

function ManageUsers() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");

  // ✅ Step 1: Define fetchUsers function outside useEffect so we can call it later
  const fetchUsers = async () => {
    try {
      setError(""); // Clear errors before fetching
      const response = await fetch("https://us-central1-officialimages-523a5.cloudfunctions.net/getUsers");
      if (!response.ok) throw new Error("Failed to fetch users.");
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err.message || "Failed to fetch users.");
    }
  };

  // ✅ Step 2: Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []); // Runs only on mount

  // ✅ Step 3: Call fetchUsers after creating a user
  const handleCreateUser = async () => {
    try {
      setError("");
      setSuccess("");

      const response = await fetch("https://us-central1-officialimages-523a5.cloudfunctions.net/createUser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to create user.");

      setSuccess(`User created successfully! User ID: ${data.uid}`);
      setEmail("");
      setRole("user");

      fetchUsers(); // ✅ Refresh user list after successful creation
    } catch (err) {
      setError(err.message || "Failed to create user.");
    }
  };

  const handlePasswordReset = async () => {
    try {
      setError("");
      setSuccess("");

      const auth = getAuth();
      await sendPasswordResetEmail(auth, selectedUser);

      setSuccess("Password reset email sent successfully!");
    } catch (err) {
      setError(err.message || "Failed to send password reset email.");
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: "auto", p: 3, bgcolor: "white", boxShadow: 3, borderRadius: 2 }}>
      <Typography variant="h5" gutterBottom>
        Add New User
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <TextField label="User Email" variant="outlined" fullWidth sx={{ my: 2 }} value={email} onChange={(e) => setEmail(e.target.value)} />

      <TextField label="Role" select fullWidth value={role} onChange={(e) => setRole(e.target.value)} sx={{ mb: 2 }}>
        <MenuItem value="user">User</MenuItem>
        <MenuItem value="admin">Admin</MenuItem>
      </TextField>

      <Button variant="contained" color="primary" fullWidth onClick={handleCreateUser} disabled={!email || !role}>
        Create User
      </Button>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Manage Existing Users
      </Typography>

      <TextField label="Select User" select fullWidth value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} sx={{ mb: 2 }}>
        {users.map((user) => (
          <MenuItem key={user.uid} value={user.email}>
            {user.email}
          </MenuItem>
        ))}
      </TextField>

      <Button variant="contained" color="secondary" fullWidth onClick={handlePasswordReset} disabled={!selectedUser}>
        Send Password Reset Email
      </Button>
    </Box>
  );
}

export default ManageUsers;
